import { horarioPatronDiasParaGuardar } from "@/components/HorarioPatronDiasEditor";

export const MODO_FIJO = "fijo";
export const MODO_FLEXIBLE = "flexible";
export const MODO_PATRON_TURNOS = "patron_turnos";

export const MODOS_HORARIO = [
  { id: MODO_FIJO, label: "Horario fijo", desc: "Franjas por día con control de entrada y salida." },
  {
    id: MODO_FLEXIBLE,
    label: "Horario flexible",
    desc: "Horario libre: ventana diaria sin hora fija; cómputo semanal o mensual y horas extra.",
  },
  {
    id: MODO_PATRON_TURNOS,
    label: "Patrones de turnos y días libres",
    desc: "Turnos fijos con días de descanso rotativos (6×2, 5×2, etc.).",
  },
];

export const PATRONES_TURNO = [
  { id: "6x2", label: "6×2 — 6 días trabajo, 2 libres", dias_trabajo: 6, dias_descanso: 2 },
  { id: "5x2", label: "5×2 — 5 días trabajo, 2 libres", dias_trabajo: 5, dias_descanso: 2 },
  { id: "4x3", label: "4×3 — 4 días trabajo, 3 libres", dias_trabajo: 4, dias_descanso: 3 },
  { id: "4x2", label: "4×2 — 4 días trabajo, 2 libres", dias_trabajo: 4, dias_descanso: 2 },
  { id: "3x2", label: "3×2 — 3 días trabajo, 2 libres", dias_trabajo: 3, dias_descanso: 2 },
  { id: "personalizado", label: "Personalizado", dias_trabajo: 5, dias_descanso: 2 },
];

export const DEFAULT_FLEXIBLE = {
  margen_entrada_antes_minutos: 0,
  computo: "semana",
  horas_objetivo_semana: 40,
  horas_objetivo_mes: 160,
  autocierre_activo: true,
  autocierre_horas_trabajo: 12,
  alerta_horas_jornada: 9,
};

/** Días laborables por semana que se asumen para estimar el objetivo diario en la pantalla. */
export const DIAS_LAB_FLEXIBLE_ESTIMADO = 5;
/** Fuera de este rango (h/día) se avisa al usuario; nunca se bloquea el guardado. */
export const OBJETIVO_DIARIO_MIN_H = 3;
export const OBJETIVO_DIARIO_MAX_H = 12;

/** Horas objetivo por día que resultan del cómputo flexible (semana o mes), con 5 días laborables. */
export function objetivoDiarioFlexible(flex) {
  const f = { ...DEFAULT_FLEXIBLE, ...(flex || {}) };
  const horasSemana =
    f.computo === "mes" ? (Number(f.horas_objetivo_mes) || 0) / (52 / 12) : Number(f.horas_objetivo_semana) || 0;
  return horasSemana / DIAS_LAB_FLEXIBLE_ESTIMADO;
}

export function objetivoDiarioInusual(horasDia) {
  return horasDia < OBJETIVO_DIARIO_MIN_H || horasDia > OBJETIVO_DIARIO_MAX_H;
}

export function fmtHorasDia(h) {
  return `${(Math.round(h * 100) / 100).toLocaleString("es-ES")} h`;
}

/** Mensaje de confirmación si el objetivo diario es poco habitual (o null). El usuario decide siempre. */
export function mensajeObjetivoFlexible(modoConfig) {
  if (!modoConfig || modoConfig.modo !== MODO_FLEXIBLE) return null;
  const flex = modoConfig.flexible || {};
  const h = objetivoDiarioFlexible(flex);
  if (!objetivoDiarioInusual(h)) return null;
  const origen =
    flex.computo === "mes" ? `${flex.horas_objetivo_mes} h al mes` : `${flex.horas_objetivo_semana} h a la semana`;
  return (
    `Con ${origen} el objetivo diario sale en ${fmtHorasDia(h)} (suponiendo ${DIAS_LAB_FLEXIBLE_ESTIMADO} días laborables).\n\n` +
    "Esto afecta a las horas previstas, las horas extra y las ausencias de día completo. " +
    "Para 8 h al día suelen ser 40 h a la semana o 160 h al mes.\n\n" +
    "¿Guardar de todas formas? (Cancelar para revisarlo)"
  );
}

function genId() {
  return `t${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export function inicioAnioIso() {
  return `${new Date().getFullYear()}-01-01`;
}

export function modoFromHorario(h) {
  if (!h || typeof h !== "object") return MODO_FIJO;
  return MODOS_HORARIO.some((m) => m.id === h.modo) ? h.modo : MODO_FIJO;
}

export function flexibleFromHorario(h) {
  return { ...DEFAULT_FLEXIBLE, ...(h?.flexible || {}) };
}

function firmaTramoDia(cfg) {
  if (!cfg) return "";
  const fr = cfg.turnos?.length
    ? cfg.turnos
    : cfg.inicio && cfg.fin
      ? [{ inicio: cfg.inicio, fin: cfg.fin }]
      : [];
  return fr.filter((t) => t?.inicio && t?.fin).map((t) => `${t.inicio}-${t.fin}`).join("|");
}

/** Si no viene el flag, se deduce: horas distintas entre días → el tramo varía. */
export function tramoVariableDeTurno(turno, nDias) {
  if (typeof turno?.tramo_variable === "boolean") return turno.tramo_variable;
  const h = turno?.horario || {};
  const n = Math.max(1, Number(nDias) || 5);
  const firmas = Array.from({ length: n }, (_, i) => firmaTramoDia(h[`d${i + 1}`])).filter(Boolean);
  if (firmas.length < 2) return false;
  return firmas.some((f) => f !== firmas[0]);
}

export function metricasTurnoPatron(turno, patron) {
  const globalTrab = Number(patron?.dias_trabajo) || 5;
  const globalDesc = Number(patron?.dias_descanso) || 2;
  if (!turno?.patron_propio) {
    return { dias_trabajo: globalTrab, dias_descanso: globalDesc, tipo: patron?.tipo || "5x2" };
  }
  const tipo = turno.tipo || "personalizado";
  const preset = PATRONES_TURNO.find((p) => p.id === tipo);
  if (preset && tipo !== "personalizado") {
    return { dias_trabajo: preset.dias_trabajo, dias_descanso: preset.dias_descanso, tipo };
  }
  return {
    dias_trabajo: Number(turno.dias_trabajo) || globalTrab,
    dias_descanso: Number(turno.dias_descanso) || globalDesc,
    tipo: "personalizado",
  };
}

export function patronFromHorario(h) {
  const tipo = h?.patron_turnos?.tipo || "5x2";
  const preset = PATRONES_TURNO.find((p) => p.id === tipo) || PATRONES_TURNO[1];
  const diasTrabajo = h?.patron_turnos?.dias_trabajo ?? preset.dias_trabajo;
  const diasDescanso = h?.patron_turnos?.dias_descanso ?? preset.dias_descanso;
  const crudos = h?.patron_turnos?.turnos?.length
    ? h.patron_turnos.turnos
    : [{ id: genId(), nombre: "Mañana", horario: null }];
  const turnos = crudos.map((t) => {
    const met = metricasTurnoPatron(
      t,
      { tipo, dias_trabajo: diasTrabajo, dias_descanso: diasDescanso }
    );
    return {
      ...t,
      patron_propio: !!t.patron_propio,
      tipo: met.tipo,
      dias_trabajo: met.dias_trabajo,
      dias_descanso: met.dias_descanso,
      tramo_variable: tramoVariableDeTurno(t, met.dias_trabajo),
    };
  });
  let seq = [...(h?.patron_turnos?.secuencia_turnos || [])].filter((id) =>
    turnos.some((t) => t.id === id)
  );
  // Migrar formato antiguo (1 id por día de trabajo) → bloques
  if (seq.length === diasTrabajo && diasTrabajo > 1) {
    const collapsed = [];
    for (const tid of seq) {
      if (!collapsed.length || collapsed[collapsed.length - 1] !== tid) collapsed.push(tid);
    }
    seq = collapsed;
  }
  if (!seq.length) seq = turnos.map((t) => t.id);
  return {
    tipo,
    dias_trabajo: diasTrabajo,
    dias_descanso: diasDescanso,
    fecha_inicio_ciclo: h?.patron_turnos?.fecha_inicio_ciclo || inicioAnioIso(),
    inicio_ciclo_modo: h?.patron_turnos?.inicio_ciclo_modo === "semana" ? "semana" : "fecha",
    festivos_laborables: !!h?.patron_turnos?.festivos_laborables,
    descanso_minimo_entre_jornadas_horas: h?.patron_turnos?.descanso_minimo_entre_jornadas_horas ?? 12,
    descanso_semanal_horas: h?.patron_turnos?.descanso_semanal_horas ?? 36,
    pausa_minima_minutos: h?.patron_turnos?.pausa_minima_minutos ?? 15,
    preaviso_cambio_dias: h?.patron_turnos?.preaviso_cambio_dias ?? 5,
    margen_entrada_antes_minutos: h?.patron_turnos?.margen_entrada_antes_minutos ?? 15,
    turnos,
    secuencia_turnos: seq,
  };
}

export function resumenModoHorario(h) {
  if (!h) return "Horario fijo";
  const modo = modoFromHorario(h);
  if (modo === MODO_FLEXIBLE) {
    const flex = flexibleFromHorario(h);
    const comp = flex.computo === "mes" ? "mes" : "semana";
    const horas = comp === "semana" ? flex.horas_objetivo_semana : flex.horas_objetivo_mes;
    return `Horario flexible · ${horas} h/${comp}`;
  }
  if (modo === MODO_PATRON_TURNOS) {
    const p = patronFromHorario(h);
    if ((p.turnos || []).some((t) => t.patron_propio)) {
      const partes = p.turnos.map((t) => `${t.nombre} ${t.dias_trabajo}×${t.dias_descanso}`);
      return `Patrón turnos · ${partes.join(" → ")}`;
    }
    const lbl = PATRONES_TURNO.find((x) => x.id === p.tipo)?.label || p.tipo;
    return `Patrón turnos · ${lbl}`;
  }
  return "Horario fijo";
}

export function modoConfigFromHorario(h) {
  return {
    modo: modoFromHorario(h),
    flexible: flexibleFromHorario(h),
    patron: patronFromHorario(h),
  };
}

/** Serializa el patrón, incluido el ciclo propio de cada turno y si el tramo varía. */
export function patronTurnosParaGuardar(p) {
  const diasGlobal = p.dias_trabajo || 5;
  return {
    tipo: p.tipo,
    dias_trabajo: p.dias_trabajo,
    dias_descanso: p.dias_descanso,
    fecha_inicio_ciclo: p.fecha_inicio_ciclo,
    inicio_ciclo_modo: p.inicio_ciclo_modo === "semana" ? "semana" : "fecha",
    festivos_laborables: !!p.festivos_laborables,
    descanso_minimo_entre_jornadas_horas: p.descanso_minimo_entre_jornadas_horas,
    descanso_semanal_horas: p.descanso_semanal_horas,
    pausa_minima_minutos: p.pausa_minima_minutos,
    preaviso_cambio_dias: p.preaviso_cambio_dias,
    margen_entrada_antes_minutos: p.margen_entrada_antes_minutos ?? 15,
    turnos: (p.turnos || []).map((t) => {
      const met = metricasTurnoPatron(t, p);
      return {
        id: t.id,
        nombre: (t.nombre || "").trim() || "Turno",
        patron_propio: !!t.patron_propio,
        tipo: met.tipo,
        dias_trabajo: met.dias_trabajo,
        dias_descanso: met.dias_descanso,
        tramo_variable: !!t.tramo_variable,
        horario: horarioPatronDiasParaGuardar(t.horario, met.dias_trabajo || diasGlobal),
      };
    }),
    secuencia_turnos: p.secuencia_turnos?.length
      ? p.secuencia_turnos
      : (p.turnos || []).map((t) => t.id),
  };
}
